// MP3 export utility for the metronome.
//
// Strategy: live capture via MediaRecorder. The metronome's scheduler runs
// normally — every click, drone, and harmony note plays through the live
// AudioContext (and Tone.js's context for sampler-based drones). We tap
// both with a MediaStreamDestination, route to MediaRecorder, then decode
// the resulting WebM and encode to MP3 with lamejs.
//
// Two contexts are involved when Tone runs on its own AudioContext. We
// bridge by creating a MediaStreamDestination on Tone's context, then
// pulling its stream into the metronome's context via MediaStreamSource.

import * as Tone from 'tone';
import lamejs from '@breezystack/lamejs';

const MP3_KBPS = 192;

// ---------------------------------------------------------------- recording

/**
 * Wires up live audio capture on the metronome's AudioContext, bridging in
 * Tone's context if it's separate. Returns a controller with start/stop and
 * a method to retrieve the final MP3 blob.
 *
 * The caller is responsible for triggering metronome playback after start()
 * and calling stop() when the desired range has played.
 *
 * Silent capture: sets the metronome's master gain to 0 and mutes Tone's
 * destination — the recorder taps a separate gain that stays at 1.
 */
export function createMp3Recorder({ audioCtx, masterGainNode, kbps = MP3_KBPS }) {
  if (!audioCtx) throw new Error('createMp3Recorder: audioCtx required');
  if (!masterGainNode) throw new Error('createMp3Recorder: masterGainNode required');

  // The recorder tap: a gain node at unity that masterGainNode mirrors into.
  // We can't silence the speakers by zeroing masterGainNode itself — the tap
  // is downstream of masterGainNode's internal gain stage, so that would
  // silence the recording too. Instead, splice a separate "speaker gain"
  // node into the speaker path that we can mute without affecting the tap.
  const recorderTap = audioCtx.createGain();
  recorderTap.gain.value = 1;
  const streamDest = audioCtx.createMediaStreamDestination();
  recorderTap.connect(streamDest);

  const speakerGain = audioCtx.createGain();
  speakerGain.gain.value = 1;
  try { masterGainNode.disconnect(audioCtx.destination); } catch {}
  masterGainNode.connect(speakerGain);
  speakerGain.connect(audioCtx.destination);
  masterGainNode.connect(recorderTap);

  // Bridge Tone's context if it differs from audioCtx.
  let toneStreamDest = null;
  let toneBridgeSource = null;
  const toneRawCtx = Tone.getContext().rawContext || Tone.getContext()._context;
  const sameContext = toneRawCtx === audioCtx;

  if (!sameContext) {
    try {
      // Create a MediaStreamDestination on Tone's context. Tone's Destination
      // (the master output) is just a node — connect it to our stream dest.
      toneStreamDest = toneRawCtx.createMediaStreamDestination();
      Tone.getDestination().connect(toneStreamDest);

      // Pull that stream into the metronome's context and route to the tap.
      toneBridgeSource = audioCtx.createMediaStreamSource(toneStreamDest.stream);
      toneBridgeSource.connect(recorderTap);
    } catch (err) {
      console.warn('[exportToMp3] Could not bridge Tone context — sampler drones / harmony may not be captured:', err);
    }
  }

  // For silent capture: zero the speaker-only gain stage. masterGainNode and
  // the tap stay at unity, so the recording keeps full signal.
  //
  // Tone's Destination is the one exception: muting it would also silence
  // the bridge we use to pull samplers / harmony into the recorder, so for
  // Tone-routed sound (sampler drones, background tracks) we accept that
  // it stays audible during export. The vast majority of metronome clicks
  // and oscillator drones go through the raw Web Audio path and ARE silenced.
  const muteSpeakers = () => {
    try { speakerGain.gain.setValueAtTime(0, audioCtx.currentTime); } catch {}
  };

  const restoreSpeakers = () => {
    try { speakerGain.gain.setValueAtTime(1, audioCtx.currentTime); } catch {}
  };

  // Pick a MIME type the browser supports. Most Chromium / Firefox give us
  // audio/webm;codecs=opus; Safari falls back to audio/mp4.
  const mimeCandidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m));
  if (!mimeType) {
    throw new Error('No supported MediaRecorder MIME type found in this browser');
  }

  const recorder = new MediaRecorder(streamDest.stream, { mimeType });
  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  let stopResolve = null;
  const stopPromise = new Promise((resolve) => { stopResolve = resolve; });
  recorder.onstop = () => stopResolve(new Blob(chunks, { type: mimeType }));

  const cleanupGraph = () => {
    // Unwind the speakerGain splice so masterGainNode goes back to feeding
    // audioCtx.destination directly — exactly the topology App.js set up.
    try { masterGainNode.disconnect(recorderTap); } catch {}
    try { masterGainNode.disconnect(speakerGain); } catch {}
    try { speakerGain.disconnect(); } catch {}
    try { masterGainNode.connect(audioCtx.destination); } catch {}

    if (toneBridgeSource) {
      try { toneBridgeSource.disconnect(); } catch {}
    }
    if (toneStreamDest) {
      try { Tone.getDestination().disconnect(toneStreamDest); } catch {}
    }
    try { recorderTap.disconnect(); } catch {}
  };

  return {
    /** Begin capturing. Caller should trigger metronome playback after this. */
    start({ silent = true } = {}) {
      if (silent) muteSpeakers();
      recorder.start(250); // 250ms chunks
    },
    /** Stop capture, restore speakers, return the WebM blob. */
    async stop() {
      if (recorder.state !== 'inactive') recorder.stop();
      const blob = await stopPromise;
      restoreSpeakers();
      cleanupGraph();
      return blob;
    },
    /** Encode the captured WebM blob to an MP3 blob. */
    encodeToMp3: (webmBlob, onProgress) => webmBlobToMp3(webmBlob, audioCtx, kbps, onProgress),
    /** Abandon capture without producing a file (e.g. on cancel). */
    abort() {
      try { if (recorder.state !== 'inactive') recorder.stop(); } catch {}
      restoreSpeakers();
      cleanupGraph();
    },
    /** Total seconds the source has been recording (approximate). */
    get mimeType() { return mimeType; },
  };
}

// ---------------------------------------------------------------- encoding

/**
 * Decode a WebM/Opus/MP4 blob (whatever MediaRecorder produced) to raw PCM
 * via the existing AudioContext, then encode to MP3 with lamejs. Returns a
 * Blob of MIME type audio/mpeg.
 *
 * onProgress is called with values in [0, 1] as encoding proceeds.
 */
export async function webmBlobToMp3(blob, audioCtx, kbps = MP3_KBPS, onProgress) {
  const arrayBuf = await blob.arrayBuffer();
  // decodeAudioData on a context that may be closing — use a fresh one if so
  let decodeCtx = audioCtx;
  if (!audioCtx || audioCtx.state === 'closed') {
    decodeCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  const audioBuffer = await decodeCtx.decodeAudioData(arrayBuf.slice(0));
  return audioBufferToMp3(audioBuffer, kbps, onProgress);
}

/**
 * Encode an AudioBuffer to MP3 with lamejs. Float32 → Int16 conversion is
 * done in blocks; onProgress fires per block.
 */
export function audioBufferToMp3(audioBuffer, kbps = MP3_KBPS, onProgress) {
  const channels = Math.min(audioBuffer.numberOfChannels, 2);
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.length;
  const encoder = new lamejs.Mp3Encoder(channels, sampleRate, kbps);

  const left = floatToInt16(audioBuffer.getChannelData(0));
  const right = channels === 2 ? floatToInt16(audioBuffer.getChannelData(1)) : null;

  const blockSize = 1152; // lamejs requires multiples of 576; 1152 is standard
  const chunks = [];
  for (let i = 0; i < samples; i += blockSize) {
    const leftBlock = left.subarray(i, Math.min(i + blockSize, samples));
    let mp3buf;
    if (right) {
      const rightBlock = right.subarray(i, Math.min(i + blockSize, samples));
      mp3buf = encoder.encodeBuffer(leftBlock, rightBlock);
    } else {
      mp3buf = encoder.encodeBuffer(leftBlock);
    }
    if (mp3buf.length > 0) chunks.push(mp3buf);
    if (onProgress && i % (blockSize * 32) === 0) {
      onProgress(Math.min(1, i / samples));
    }
  }
  const tail = encoder.flush();
  if (tail.length > 0) chunks.push(tail);
  if (onProgress) onProgress(1);

  return new Blob(chunks, { type: 'audio/mpeg' });
}

function floatToInt16(floatArr) {
  const out = new Int16Array(floatArr.length);
  for (let i = 0; i < floatArr.length; i++) {
    const s = Math.max(-1, Math.min(1, floatArr[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

// ---------------------------------------------------------------- download

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the download has time to start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Build a filename like "FlowFrame-2026-05-24.mp3" or, if a pattern name is
 * supplied, "FlowFrame-MyPattern-2026-05-24.mp3".
 */
export function defaultMp3Filename(patternName) {
  const date = new Date().toISOString().slice(0, 10);
  const safeName = patternName
    ? patternName.replace(/[^\w\-]+/g, '_').slice(0, 60)
    : '';
  return safeName
    ? `FlowFrame-${safeName}-${date}.mp3`
    : `FlowFrame-${date}.mp3`;
}
